import type { HealthcarePetSummary, HealthcareServiceRecord } from "@/type/healthcare";

/**
 * Display helpers for the service-provider floor screen: which cartoon a service gets, and how
 * long the pet has been with us.
 *
 * Pure functions — no i18n, no Vue — so the queue card, the stage card and the dialogs all agree.
 */

export type ServiceScene = "bath" | "groom" | "care";
export type ServiceAnimal = "dog" | "cat" | "other";

/**
 * Bath is checked BEFORE groom on purpose: a package such as "تحميم وتمشيط" is a bath first, and
 * the bath scene is the one a guardian recognises from across the room.
 *
 * Matched against the catalogue's own wording — "حلاقة كلب 25", "تحميم كلاب 35", "حلاقة بزون"
 * (`careServiceBillingOptionsApi.ts`) — plus English for the categories that were named that way.
 */
const BATH_PATTERN = /استحمام|تحميم|حمام|حمّام|غسل|bath|wash|shampoo/i;
// `\bspa\b` whole-word: a bare `spa` would catch "spay", which is surgery, not grooming.
const GROOM_PATTERN = /حلاق|قص|تجميل|تمشيط|تشذيب|groom|haircut|\bcut\b|trim|\bspa\b|styling/i;

export function serviceScene(service: HealthcareServiceRecord | null | undefined): ServiceScene {
  if (!service)
    return "care";

  const haystack = [
    service.serviceName,
    service.careServiceName,
    service.categoryName,
    service.category,
    service.itemCode,
    service.careServiceId,
  ].map(value => String(value ?? "")).join(" ");

  if (BATH_PATTERN.test(haystack))
    return "bath";
  if (GROOM_PATTERN.test(haystack))
    return "groom";

  return "care";
}

export function serviceAnimal(pet: Partial<HealthcarePetSummary> | null | undefined): ServiceAnimal {
  const text = `${pet?.animalType ?? ""} ${pet?.species ?? ""}`.toLowerCase();

  if (/dog|canine|puppy|كلب|جرو/.test(text))
    return "dog";
  if (/cat|feline|kitten|قط|قطة|بزون|بسة/.test(text))
    return "cat";

  return "other";
}

const NAIVE_DATETIME = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/;

/** Milliseconds the zone is AHEAD of UTC at the given instant (Asia/Baghdad → +3h). */
function zoneOffsetMs(zone: string, at: number) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(at));

  const read = (type: string) => Number(parts.find(part => part.type === type)?.value ?? 0);
  const asUtc = Date.UTC(read("year"), read("month") - 1, read("day"), read("hour"), read("minute"), read("second"));

  return asUtc - (at - (at % 1000));
}

/**
 * A backend datetime (`2026-10-01 10:42:07.123456`, no offset) as an epoch.
 *
 * ⚠️ The string is wall-clock time in the SERVER's zone. Reading it with `new Date()` treats it as
 * the iPad's zone, so a device set one zone over would start every timer an hour early or late —
 * a live counter is exactly where that shows. With no zone resolved this falls back to that
 * browser-local reading, which is what the page did before.
 */
export function serverDateTimeToEpoch(raw: string | null | undefined, zone: string | null): number | null {
  const value = String(raw ?? "").trim();
  if (!value)
    return null;

  const match = value.match(NAIVE_DATETIME);
  if (!match) {
    const parsed = new Date(value).getTime();

    return Number.isNaN(parsed) ? null : parsed;
  }

  const [, y, mo, d, h, mi, s] = match;
  const wallAsUtc = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s ?? 0));

  if (zone) {
    try {
      // Two passes: the first offset is taken at a guess that can sit on the wrong side of a DST
      // switch; re-reading it at the corrected instant settles it.
      const first = wallAsUtc - zoneOffsetMs(zone, wallAsUtc);

      return wallAsUtc - zoneOffsetMs(zone, first);
    } catch {
      // Unknown zone — fall through to the browser's reading.
    }
  }

  return new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s ?? 0)).getTime();
}

/**
 * When the work actually ended.
 *
 * `end_date` when the backend stamped it. For a service that is already DONE without one, the
 * row's `modified` stands in: completing a service submits it in the same breath, and a submitted
 * row is not edited afterwards, so `modified` is the completion moment to within seconds. An
 * unfinished service never borrows it — its timer keeps running.
 */
export function serviceEndEpoch(service: HealthcareServiceRecord, zone: string | null, isDone: boolean): number | null {
  return serverDateTimeToEpoch(service.endDate, zone)
    ?? (isDone ? serverDateTimeToEpoch(service.modifiedAt, zone) : null);
}

/**
 * How long the pet has been (or was) with us. `null` when the work has not started.
 * Never negative: a device clock a little behind the server would otherwise count down.
 */
export function serviceElapsedMs(
  service: HealthcareServiceRecord,
  nowMs: number,
  zone: string | null,
  isDone = false,
): number | null {
  const start = serverDateTimeToEpoch(service.startDate, zone);
  if (start === null)
    return null;

  const end = serviceEndEpoch(service, zone, isDone) ?? (isDone ? null : nowMs);
  if (end === null)
    return null;

  return Math.max(0, end - start);
}

/** `1:05:09` / `05:09` — the live counter. Latin digits so the width never jumps. */
export function formatClock(ms: number) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (value: number) => String(value).padStart(2, "0");

  return hours ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`;
}

/** Whole hours and minutes, for the "took 1h 05m" wording the caller words through i18n. */
export function formatDurationParts(ms: number) {
  const totalMinutes = Math.max(0, Math.round(ms / 60000));

  return { h: Math.floor(totalMinutes / 60), m: totalMinutes % 60 };
}
